import React from "react";
import styles from "./styles";
import { strings } from "../../../../common/i18n";
import { Box, Grid, Typography, Stack, Divider } from "@mui/material/";
import CheckAvatar from "../../../../common/components/CheckAvatar";
import NotesIcon from "@mui/icons-material/Notes";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import { enums } from "../../../../common/lib";

// Helper function to get styles based on heading
const getHeadingStyles = (heading) => {
    // Convert to uppercase for consistency
    const headingUpper = heading.toUpperCase();

    switch (headingUpper) {
        // APPROVAL/REJECTION/EXCLUSION
        case "ASSET REJECTED":
            return {
                color: "error.dark", // Red
                fontWeight: 500,
                letterSpacing: "0.3px"
            };
        case "ASSET APPROVED":
            return {
                color: "success.dark",
                fontWeight: 500,
                letterSpacing: "0.3px"
            };
        case "ASSET EXCLUDED":
            return {
                color: "warning.dark",
                fontWeight: 500,
                letterSpacing: "0.3px"
            };

        case "ASSET COMMENT":
        case "NOTE ADDED":
            return {
                color: "info.dark",
                fontWeight: 500,
                letterSpacing: "0.3px"
            };

        case "ASSET SEEN":
            return {
                color: "success.dark",
                fontWeight: 500,
                letterSpacing: "0.3px"
            };

        case "ADDED NEW IMAGE":
            return {
                color: "warning.dark",
                fontWeight: 500,
                letterSpacing: "0.3px"
            };

        case "ADDED NEW EVIDENCE":
            return {
                color: "warning.dark",
                fontWeight: 500,
                letterSpacing: "0.3px"
            };

        default:
            // Check for keywords in the heading
            if (
                headingUpper.includes("COMMENT") ||
                headingUpper.includes("NOTE")
            ) {
                return {
                    color: "info.dark", // BLUE for comments/notes
                    fontWeight: 500,
                    letterSpacing: "0.3px"
                };
            }
            if (headingUpper.includes("SEEN")) {
                return {
                    color: "success.dark", // GREEN for SEEN
                    fontWeight: 500,
                    letterSpacing: "0.3px"
                };
            }

            if (headingUpper.includes("IMAGE")) {
                return {
                    color: "warning.dark",
                    fontWeight: 500,
                    letterSpacing: "0.3px"
                };
            }
            if (headingUpper.includes("EVIDENCE")) {
                return {
                    color: "warning.dark",
                    fontWeight: 500,
                    letterSpacing: "0.3px"
                };
            }
            if (
                headingUpper.includes("EXCLUDED") ||
                headingUpper.includes("REJECTED") ||
                headingUpper.includes("REMOVED")
            ) {
                return {
                    color: headingUpper.includes("EXCLUDED")
                        ? "warning.dark"
                        : "error.dark",
                    fontWeight: 500,
                    letterSpacing: "0.3px"
                };
            }
            if (headingUpper.includes("APPROVED")) {
                return {
                    color: "success.dark", // Green
                    fontWeight: 500,
                    letterSpacing: "0.3px"
                };
            }

            // Default for any other headers
            return {
                color: "text.secondary", // Grey only for truly unknown headers
                fontWeight: 400,
                letterSpacing: "0.3px"
            };
    }
};

// Subcomponent for rendering exclusion details
const Detail = ({ label, text, Icon, type }) => (
    <Stack
        direction="row"
        alignItems="flex-start"
        spacing={0.5}
        sx={{ mb: 0.25 }}
    >
        <Icon
            fontSize="inherit"
            sx={{
                color: `${type}.main`,
                fontSize: "11px",
                mt: "2px",
                flexShrink: 0,
                opacity: 0.8
            }}
        />
        <Typography
            variant="caption"
            color="text.secondary"
            sx={{
                whiteSpace: "nowrap",
                fontSize: "0.7rem",
                opacity: 0.8
            }}
        >
            {strings(label)}:
        </Typography>
        <Typography
            variant="caption"
            color="text.primary"
            sx={{
                flex: 1,
                fontSize: "0.7rem",
                opacity: 0.9
            }}
        >
            {text}
        </Typography>
    </Stack>
);

const AssetHistoryItem = ({ assetHistory, isLast }) => {
    const {
        user,
        note,
        exclusionReason,
        exclusionReasonDate,
        date,
        rejectionDate,
        heading,
        rejectionReason,
        imageDetail,
        documentDetail,
        systemActorKey
    } = assetHistory;

    const actorName = systemActorKey
        ? strings(systemActorKey)
        : user.nameKey
        ? strings(user.nameKey)
        : user.name;

    const exclusionDatePickers = [
        enums.status.exclusionReason.assetIsSold,
        enums.status.exclusionReason.assetDeliveredToCustomerAndNotPaid
    ];
    const isPicker =
        exclusionReason && exclusionDatePickers.includes(exclusionReason.id);

    const headingStyles = getHeadingStyles(heading);

    return (
        <Box sx={{ py: 0.35 }}>
            <Grid container spacing={0.75} alignItems="flex-start">
                <Grid item xs={1}>
                    <Box display="flex" justifyContent="center">
                        <CheckAvatar
                            name={actorName}
                            url={user.avatarPath}
                            clsName={heading}
                            size={16}
                            sx={{
                                width: 16,
                                height: 16,
                                fontSize: "0.65rem",
                                // Apply matching color to avatar
                                bgcolor: headingStyles.color
                                    ? `${headingStyles.color.replace(
                                          ".dark",
                                          ".light"
                                      )}`
                                    : "grey.200"
                            }}
                        />
                    </Box>
                </Grid>

                {/* Main Content Column */}
                <Grid item xs={11}>
                    {/* Single Line Header */}
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={0.75}
                        flexWrap="wrap"
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                textTransform: "uppercase",
                                fontSize: "0.7rem",
                                lineHeight: 1.2,
                                // Apply the color from headingStyles
                                ...headingStyles
                            }}
                        >
                            {strings(heading)}
                        </Typography>

                        <Box
                            component="span"
                            sx={{
                                fontSize: "0.7rem",
                                color: "text.disabled",
                                lineHeight: 1.2
                            }}
                        >
                            ·
                        </Box>

                        <Typography
                            variant="body2"
                            color="text.primary"
                            fontWeight={500}
                            sx={{ fontSize: "0.8rem" }}
                        >
                            {actorName}
                        </Typography>

                        {date && (
                            <>
                                <Box
                                    component="span"
                                    sx={{
                                        fontSize: "0.7rem",
                                        color: "text.disabled",
                                        lineHeight: 1.2
                                    }}
                                >
                                    ·
                                </Box>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontSize: "0.7rem" }}
                                >
                                    {date}
                                </Typography>
                            </>
                        )}
                    </Box>

                    {/* Details Section */}
                    {(rejectionReason ||
                        exclusionReason ||
                        note ||
                        documentDetail ||
                        imageDetail) && (
                        <Box
                            sx={{
                                pl: 1,
                                borderLeft: "0.75px solid",
                                borderColor: "divider",
                                ml: 0.5,
                                mt: 0.35
                            }}
                        >
                            <Stack spacing={0.25}>
                                {rejectionReason && (
                                    <>
                                        <Detail
                                            label="reason"
                                            text={rejectionReason}
                                            Icon={BlockOutlinedIcon}
                                            type="error"
                                        />
                                        {rejectionDate && (
                                            <Detail
                                                label="rejectionDate"
                                                text={rejectionDate}
                                                Icon={CalendarTodayIcon}
                                                type="primary"
                                            />
                                        )}
                                    </>
                                )}

                                {exclusionReason && (
                                    <Detail
                                        label="reason"
                                        text={exclusionReason.name}
                                        Icon={RemoveCircleOutlineIcon}
                                        type="error"
                                    />
                                )}

                                {documentDetail && (
                                    <Detail
                                        label="evidence"
                                        text={documentDetail}
                                        Icon={DescriptionOutlinedIcon}
                                        type="info"
                                    />
                                )}

                                {imageDetail && (
                                    <Detail
                                        label="type"
                                        text={imageDetail}
                                        Icon={ImageOutlinedIcon}
                                        type="info"
                                    />
                                )}

                                {note && (
                                    <Detail
                                        label="note"
                                        text={note}
                                        Icon={NotesIcon}
                                        type="info"
                                    />
                                )}

                                {isPicker && exclusionReasonDate === "" && (
                                    <Detail
                                        label="date"
                                        text={strings("noDateAvailable")}
                                        Icon={EventBusyIcon}
                                        type="info"
                                    />
                                )}

                                {exclusionReasonDate && (
                                    <Detail
                                        label="date"
                                        text={exclusionReasonDate}
                                        Icon={CalendarTodayIcon}
                                        type="primary"
                                    />
                                )}
                            </Stack>
                        </Box>
                    )}
                </Grid>
            </Grid>

            {/* Subtle divider */}
            {!isLast && (
                <Divider
                    sx={{
                        mt: 0.5,
                        opacity: 0.2,
                        height: "0.75px"
                    }}
                />
            )}
        </Box>
    );
};

export default AssetHistoryItem;
